/**
 * useNotificationEvents — Listens to OS events and creates notification items.
 * Feeds into NotificationCenter's "Live Events" section.
 * Rule 94: Modüler — event listener ayrı hook.
 * Rule 58: Agent Bus event'leri dinler.
 */
import { useCallback, useEffect, useState } from 'react'

export interface LiveNotification {
  id: string
  type: 'task' | 'skill' | 'research' | 'social' | 'flzhub' | 'system'
  title: string
  message: string
  timestamp: Date
  read: boolean
  actionEvent?: string
}

export function useNotificationEvents() {
  const [liveNotifications, setLiveNotifications] = useState<LiveNotification[]>([])

  const add = useCallback((n: Omit<LiveNotification, 'id' | 'timestamp' | 'read'>) => {
    setLiveNotifications(prev => [{
      ...n,
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      timestamp: new Date(),
      read: false,
    }, ...prev].slice(0, 30))
  }, [])

  useEffect(() => {
    const handlers: Array<[string, (e: Event) => void]> = [
      ['runai:task-alert', (e: Event) => {
        const d = (e as CustomEvent).detail
        add({
          type: 'task',
          title: d.type === 'completed' ? 'Task Completed' : d.type === 'deadline' ? 'Deadline' : 'New Task',
          message: d.title ?? 'Task update',
          actionEvent: 'task-manager',
        })
      }],
      ['runai:skill-result', (e: Event) => {
        const d = (e as CustomEvent).detail
        add({
          type: 'skill',
          title: d.status === 'failed' ? 'Skill Failed' : 'Skill Done',
          message: d.skillName ?? 'Skill result',
        })
      }],
      ['runai:research-finding', (e: Event) => {
        const d = (e as CustomEvent).detail
        add({
          type: 'research',
          title: 'Research Finding',
          message: d.title ?? 'New finding',
          actionEvent: 'research-window',
        })
      }],
      ['runai:social-mention', (e: Event) => {
        const d = (e as CustomEvent).detail
        add({
          type: 'social',
          title: `${d.author ?? 'Someone'} mentioned you`,
          message: (d.content ?? '').slice(0, 80),
          actionEvent: 'social-window',
        })
      }],
      ['runai:flzhub-message', (e: Event) => {
        const d = (e as CustomEvent).detail
        add({
          type: 'flzhub',
          title: d.agentName ?? 'FlzHub',
          message: (d.message ?? '').slice(0, 80),
          actionEvent: 'flzhub',
        })
      }],
    ]

    handlers.forEach(([ev, fn]) => window.addEventListener(ev, fn))
    return () => handlers.forEach(([ev, fn]) => window.removeEventListener(ev, fn))
  }, [add])

  const unreadCount = liveNotifications.filter(n => !n.read).length

  const markRead = (id: string) => {
    setLiveNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n))
  }

  const markAllRead = () => {
    setLiveNotifications(prev => prev.map(n => ({ ...n, read: true })))
  }

  const clearAll = () => setLiveNotifications([])

  return { liveNotifications, unreadCount, markRead, markAllRead, clearAll }
}
